<template>
    <div>
        <div class="button-inspection-details">
            <div v-for="group in groups" :key="group.id" class="mb-2">
                <b-button
                    v-b-toggle="modalId + '-group-' + group.id"
                    variant="light"
                    class="w-100 text-left d-flex justify-content-between align-items-center mb-1 group-toggle"
                >
                    <span>
                        {{ group.name }} ({{ group.defects.length }})
                        <span v-if="selectedCountIn(group)" class="badge badge-danger badge-pill ml-1">{{ selectedCountIn(group) }} ticked</span>
                    </span>
                    <i class="las la-chevron-down"></i>
                </b-button>
                <b-collapse :id="modalId + '-group-' + group.id" visible>
                    <span v-for="defect in group.defects" :key="defect.id">
                        <div class="form-group mb-1">
                            <label class="btn btn-check defect-row d-flex align-items-center p-2 mb-0">
                                <input
                                    class="inspect defect-checkbox mr-2 flex-shrink-0"
                                    type="checkbox"
                                    :checked="isSelected(defect.id)"
                                    @change="toggleDefect(defect.id)"
                                >
                                <p class="mb-0 flex-grow-1">{{ defect.label }}</p>
                                <button
                                    v-if="defect.sample_image_url"
                                    type="button"
                                    class="btn btn-outline-info btn-sm ml-2 sample-image-btn flex-shrink-0"
                                    aria-label="View sample image"
                                    @click.prevent="viewSampleImage(defect)"
                                >
                                    <i class="las la-image"></i>Sample<span class="d-none d-sm-inline"> Image</span>
                                </button>
                            </label>
                        </div>
                        <div class="photo-box mb-2 rounded p-2" v-if="isSelected(defect.id)">
                            <div v-if="processing[defect.id]" class="d-flex align-items-center text-muted small py-2">
                                <span class="spinner-border spinner-border-sm mr-2" role="status"></span>Preparing photo…
                            </div>
                            <div v-else-if="previews[defect.id] || existingPictureFor(defect.id)" class="d-flex align-items-center">
                                <button
                                    type="button"
                                    class="photo-thumb-btn mr-2 flex-shrink-0"
                                    aria-label="View photo"
                                    @click="viewPhoto(defect)"
                                >
                                    <img :src="previews[defect.id] || existingPictureFor(defect.id)" class="photo-thumb rounded" alt="Defect photo">
                                </button>
                                <div class="flex-grow-1 small">
                                    <span v-if="previews[defect.id]" class="text-success d-block"><i class="las la-check-circle"></i> Photo added</span>
                                    <span v-else class="text-muted d-block">Current photo</span>
                                    <photo-source-picker
                                        compact
                                        camera-label="Retake"
                                        desktop-label="Change"
                                        @selected="file => handleFile(defect.id, file)"
                                    />
                                    <button v-if="previews[defect.id]" type="button" class="btn btn-link btn-sm text-danger p-0 photo-action" @click="removePhoto(defect.id)">Remove</button>
                                </div>
                            </div>
                            <photo-source-picker v-else @selected="file => handleFile(defect.id, file)" />
                        </div>
                    </span>
                </b-collapse>
            </div>
        </div>

        <image-lightbox ref="lightbox" />
    </div>
</template>

<script>
import compressImage from '../../utils/compressImage';

export default {
    props: {
        groups: { type: Array, required: true },
        selectedIds: { type: Array, default: () => [] },
        existingPictures: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            // previews[component_defect_id] = object URL of the newly chosen photo
            previews: {},
            processing: {},
        };
    },
    computed: {
        modalId() {
            return `defect-sample-image-modal-${this._uid}`;
        },
    },
    beforeDestroy() {
        Object.values(this.previews).forEach(url => URL.revokeObjectURL(url));
    },
    methods: {
        isSelected(defectId) {
            return this.selectedIds.includes(defectId);
        },
        selectedCountIn(group) {
            return group.defects.filter(defect => this.isSelected(defect.id)).length;
        },
        toggleDefect(defectId) {
            const next = this.selectedIds.slice();
            const idx = next.indexOf(defectId);
            if (idx === -1) {
                next.push(defectId);
            } else {
                next.splice(idx, 1);
                this.removePhoto(defectId);
            }
            this.$emit('update:selected-ids', next);
        },
        async handleFile(defectId, original) {
            this.$set(this.processing, defectId, true);
            const file = await compressImage(original);
            this.$delete(this.processing, defectId);

            this.setPreview(defectId, URL.createObjectURL(file));
            this.$emit('file-change', { componentDefectId: defectId, file });
        },
        removePhoto(defectId) {
            if (!this.previews[defectId]) {
                return;
            }
            this.setPreview(defectId, null);
            this.$emit('file-change', { componentDefectId: defectId, file: null });
        },
        setPreview(defectId, url) {
            if (this.previews[defectId]) {
                URL.revokeObjectURL(this.previews[defectId]);
            }
            if (url) {
                this.$set(this.previews, defectId, url);
            } else {
                this.$delete(this.previews, defectId);
            }
        },
        existingPictureFor(defectId) {
            return this.existingPictures[defectId] || null;
        },
        viewSampleImage(defect) {
            this.$refs.lightbox.open(defect.sample_image_url, {
                title: 'Sample: ' + defect.label,
                caption: defect.context_helper,
            });
        },
        viewPhoto(defect) {
            this.$refs.lightbox.open(this.previews[defect.id] || this.existingPictureFor(defect.id), {
                title: defect.label,
            });
        },
    },
};
</script>

<style scoped>
.photo-box {
    background: #f4f6fb;
    border: 1px dashed #cbd5e1;
}
.photo-thumb {
    width: 72px;
    height: 72px;
    object-fit: cover;
}
.photo-thumb-btn {
    padding: 0;
    border: 0;
    background: none;
    line-height: 0;
}
.photo-action {
    position: relative;
    min-height: 44px;
}
.photo-action {
    display: inline-flex;
    align-items: center;
}
.defect-checkbox {
    width: 20px;
    height: 20px;
}
.group-toggle {
    min-height: 44px;
}
/* Same height as a row with a Sample button, so the list doesn't look uneven on phones */
.defect-row {
    min-height: 60px;
}
.sample-image-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    font-size: 13px;
    white-space: nowrap;
}
.sample-image-btn i {
    font-size: 1.2em;
}
@media (min-width: 576px) {
    .sample-image-btn {
        min-width: 120px;
        min-height: 0;
    }
    .defect-row {
        min-height: 0;
    }
}
</style>
